import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  Container,
  TextField,
  Paper,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { InsertPhoto, Person } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { usePersonalInformation } from "../hooks/usePersonalInformation";
import CustomAppBar from "../components/AppBar";
import { useAlert } from "../components/Alert";
import { AddressFields } from "../components/Famer/AddressFields";
import KYCFields from "../components/Famer/KYCFields";
import GenericButton from "../components/Custom/GenericButton";
import { farmerValidationSchema } from "../components/Custom/ValidationSchema";
import api from "../globals/axios";
import {
  filterEmptyValues,
  handleMobileKeyDown,
  transformBackendErrors,
} from "../globals/utils";
import LoaderContainer from "../components/LoaderContainer";
import { CustomTitle } from "../components/Custom/CustomTypography";
import { saveAuth, saveToLocal, getAuth } from "../globals/authentication";

const validationSchema = farmerValidationSchema;

const CreateUpdateFarmer = ({ role }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [errors, setErrors] = useState({});
  const profilePictureRef = useRef(null);
  const aadharImageRef = useRef(null);
  const panImageRef = useRef(null);
  const { t } = useTranslation();
  const [vles, setVles] = useState([]);
  const [areaManagers, setAreaManagers] = useState([]);

  const {
    profileData,
    postOffices,
    pincodeError,
    handleChange,
    handleImageUpload,
    previews,
    handleClick,
    fetchAddressDetails,
    loading,
  } = usePersonalInformation({ id });

  useEffect(() => {
    if (!id) {
      if (role == "POP_MANAGER") {
        fetchAreaManagers();
        if (profileData.area_manager) {
          fetchVLEs(profileData.area_manager);
        }
      } else if (role == "AREA_MANAGER") {
        fetchVLEs();
      }
    }
  }, [role, profileData.area_manager, id]);

  const fetchAreaManagers = async () => {
    try {
      const res = await api.get(
        `/usermanagement/get-sub-users?area_managers=true`
      );
      setAreaManagers(res.data.data.area_managers);
    } catch (error) {
      console.error("Error fetching Area Managers:", error);
      showAlert("error", t("Failed to fetch area managers"));
    }
  };

  const fetchVLEs = async (area_manager_id = null) => {
    try {
      const endpoint =
        role === "POP_MANAGER"
          ? `/usermanagement/get-sub-users?area_manager_id=${area_manager_id}&vles=true`
          : `/usermanagement/get-sub-users?vles=true`;
      const res = await api.get(endpoint);
      setVles(res.data.data.vles);
    } catch (error) {
      console.error("Error fetching VLEs:", error);
      showAlert("error", t("Failed to fetch VLEs"));
    }
  };

  const handleLoginFarmer = async (id) => {
    try {
      const currentAuthData = getAuth();
      const response = await api.post(`/vle/farmer-login/?persona_user=${id}`);
      const authData = {
        ...response.data,
        redirectionURL: "/farmer/create-farm",
      };

      const encodedAuthData = encodeURIComponent(JSON.stringify(authData));

      if (currentAuthData?.is_internal_login) {
        navigate(`/login-internal/?authDetails=${encodedAuthData}`);
      } else {
        if (currentAuthData) {
          saveToLocal("auth_backup", currentAuthData);
        }
        saveAuth(authData);
        navigate(`/login-internal/?authDetails=${encodedAuthData}`);
      }
    } catch (error) {
      console.error("Error logging", error);
      showAlert("error", "Error logging in as Farmer");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pincodeError) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        pin_code: pincodeError,
      }));
      return;
    }
    try {
      const filteredData = filterEmptyValues(profileData);
      await validationSchema.validate(filteredData, {
        abortEarly: false,
      });
      const response = await handleClick(filteredData);
      handleLoginFarmer(response.data.id);
    } catch (error) {
      if (error.name === "ValidationError") {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
      } else if (error.response?.data) {
        const backendErrors = transformBackendErrors(error.response.data);
        setErrors(backendErrors);
      }
      const message = error.response?.data?.message;
      showAlert(
        "error",
        typeof message === "string"
          ? message
          : t("An error occurred while adding the Farmer.")
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>{!id ? "Add Farmer" : "Update Farmer"} - Farmapp</title>
      </Helmet>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container maxWidth="xs">
          <Grid
            container
            justify="center"
            direction="column"
            style={{ marginTop: "20px" }}
          >
            <CustomTitle variant="h5">
              {!id ? t("Add Farmer") : t("Update Farmer")}
            </CustomTitle>
            <form onSubmit={handleSubmit} style={{ marginTop: "20px" }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("Full Name")}
                    name="full_name"
                    value={profileData.full_name}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    required
                  />
                </Grid>
                {/* Contact Detail */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("Phone")}
                    name="phone_number"
                    value={profileData.phone_number}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    type="tel"
                    inputProps={{
                      maxLength: 10,
                      inputMode: "numeric",
                    }}
                    onKeyDown={handleMobileKeyDown}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">+91</InputAdornment>
                      ),
                    }}
                    error={!!errors.phone_number}
                    helperText={errors.phone_number}
                    disabled={id}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("Alternate Phone")}
                    name="alternate_phone_number"
                    value={profileData.alternate_phone_number}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    type="tel"
                    inputProps={{
                      maxLength: 10,
                      inputMode: "numeric",
                    }}
                    onKeyDown={handleMobileKeyDown}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">+91</InputAdornment>
                      ),
                    }}
                    error={!!errors.alternate_phone_number}
                    helperText={errors.alternate_phone_number}
                  />
                </Grid>
                {/* Profile Picture */}
                <Grid item xs={12}>
                  <Paper
                    elevation={3}
                    sx={{
                      width: "100%",
                      height: 200,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      position: "relative",
                    }}
                  >
                    {previews.profile_picture ? (
                      <img
                        src={previews.profile_picture || "/placeholder.svg"}
                        alt="Profile Preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                    ) : (
                      <Person sx={{ fontSize: 80, color: "#ccc" }} />
                    )}
                  </Paper>
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    ref={profilePictureRef}
                    onChange={(e) => handleImageUpload(e, "profile_picture")}
                  />
                  <GenericButton
                    padding="8px 16px"
                    borderColor="#A5A5A5"
                    onClick={() => profilePictureRef.current.click()}
                  >
                    <InsertPhoto
                      fontSize="medium"
                      sx={{ mr: 2, color: "#4CAF50" }}
                    />
                    {t("Upload Profile Picture")}
                  </GenericButton>
                </Grid>

                {/* Address Details */}
                <AddressFields
                  profileData={profileData}
                  handleChange={handleChange}
                  postOffices={postOffices}
                  pincodeError={pincodeError}
                  fetchAddressDetails={fetchAddressDetails}
                  t={t}
                  errors={errors}
                />
                {/* Manager Details */}
                {role === "POP_MANAGER" && (
                  <Grid item xs={12}>
                    <TextField
                      label={t("Area Managers")}
                      name="area_manager"
                      value={profileData.area_manager || ""}
                      onChange={(e) =>
                        handleChange(e.target.name, e.target.value)
                      }
                      fullWidth
                      error={!!errors.area_manager}
                      helperText={errors.area_manager}
                      select
                      required={true}
                    >
                      {areaManagers.map((a) => (
                        <MenuItem key={a.id} value={a.id}>
                          {a.full_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
                {["AREA_MANAGER", "POP_MANAGER"].includes(role) && (
                  <Grid item xs={12}>
                    <TextField
                      label={t("VLE")}
                      name="vle"
                      value={profileData.vle || ""}
                      onChange={(e) =>
                        handleChange(e.target.name, e.target.value)
                      }
                      fullWidth
                      error={!!errors.vle}
                      helperText={errors.vle}
                      select
                      required={true}
                    >
                      {vles.map((v) => (
                        <MenuItem key={v.id} value={v.id}>
                          {v.full_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}

                {/* Kyc Details */}
                <KYCFields
                  profileData={profileData}
                  handleChange={handleChange}
                  handleImageUpload={handleImageUpload}
                  previews={previews}
                  aadharImageRef={aadharImageRef}
                  panImageRef={panImageRef}
                  t={t}
                  errors={errors}
                  smallWidth={true}
                />

                <Grid item xs={12}>
                  <GenericButton
                    type="submit"
                    backgroundColor="primary"
                    fontColor={"#fff"}
                    fontSize={"14px"}
                    padding={"7px 10px"}
                  >
                    {id ? t("Save") : t("Add Farmer")}
                  </GenericButton>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Container>
      </LoaderContainer>
    </>
  );
};
export default CreateUpdateFarmer;
